import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { Box, IconButton } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import PhoneFrame from '@/designs-dev/device/PhoneFrame';
import TabBar from '@/designs-dev/device/TabBar';
import {
  InspectProvider,
  useInspect,
} from '@/designs-dev/inspect/InspectContext';
import DrillModeToggle from '@/designs-dev/inspect/DrillModeToggle';
import StageBar, { STAGE_BAR_ROOM } from '@/designs-dev/inspect/StageBar';
import InspectOverlay from '@/designs-dev/inspect/InspectOverlay';
import InspectPanel from '@/designs-dev/inspect/InspectPanel';
import LayersPanel from '@/designs-dev/inspect/LayersPanel';
import ScreenRoot from '@/components/wrapped/ScreenRoot';
import StatusBar from '@/designs-dev/device/StatusBar';
import AppBar from '@/karehero-app/shell/AppBar';
import HomeScreen from '@/karehero-app/home/HomeScreen';
import ArchitectureActions from '@/karehero-app/architecture/ArchitectureActions';
import ArchitectureProfile from '@/karehero-app/architecture/ArchitectureProfile';
import ArchitectureHelp from '@/karehero-app/architecture/ArchitectureHelp';
import ArchitectureCardScreen from '@/karehero-app/architecture/ArchitectureCardScreen';
import ModalScreen, {
  SETTLE_MS as MODAL_SETTLE_MS,
} from '@/designs-dev/device/ModalScreen';
import PushScreen from '@/designs-dev/device/PushScreen';
import ChatV2 from '@/karehero-app/tell-us-once/intake/ChatV2';
import CalculatorBar from '@/karehero-app/tell-us-once/benefits/CalculatorBar';
import ResultScreen from '@/karehero-app/tell-us-once/benefits/ResultScreen';
import { CALCULATOR_CHAT } from '@/karehero-app/tell-us-once/benefits/BenefitsCalculator.content';
import FinancialAssessment from '@/karehero-app/care/forms/FinancialAssessment';
import ShortlistsTab from '@/karehero-app/care/shortlists/ShortlistsTab';
import TabPlaceholder from '@/karehero-app/shell/TabPlaceholder';
import { type TabBarItem } from '@/designs-dev/device/TabBar';
import { CARE_PROFILE } from '@/karehero-app/tell-us-once/intake/content';
import { CARD_INTROS } from '@/karehero-app/architecture/Architecture.content';

// The master's five, with Chat as Help: a person is one of two answers to being
// stuck and the articles are the other, so they share a tab.
const TABS: TabBarItem[] = [
  { id: 'home', label: 'Home', icon: 'house', solid: true },
  { id: 'actions', label: 'Actions', icon: 'calendar', solid: true },
  { id: 'profile', label: 'Profile', icon: 'user', solid: false },
  { id: 'care', label: 'Care', icon: 'heart', solid: true },
  { id: 'help', label: 'Help', icon: 'circle-question', solid: false },
];

// PROPOSED. The master prototype, rearranged.
//
// A copy rather than a flag on the master: the question here is where things
// live, and an architecture you can only see by toggling something is one
// nobody can walk. Open it, tap around, and it is either better than the
// master or it is not.
//
// Home and Care are the master's own. Actions keeps its tab and loses its
// filters. Profile is everything about the person in three pills, Assess among
// them. Help is where being stuck goes.
// What a cut of the architecture hands the stage: its own tab bar and what
// each tab draws. The flows, sheets and chrome stay the stage's, so two cuts
// differ only in where things live.
export type ArchitectureCut = {
  tabs: TabBarItem[];
  screen: (
    tab: string,
    stage: {
      /** Who the app is about, as the bar's switcher last set it. */
      person: string;
      setPerson: (name: string) => void;
      setTab: (tab: string) => void;
      setIntro: (card: string) => void;
      /** Bumped when a flow finishes, to remount the tab it lands on. */
      assessKey: number;
    },
  ) => ReactNode;
  /** The tab a finished flow lands on, where its claim now lives. */
  assessTab: string;
};

const Stage = ({ cut }: { cut?: ArchitectureCut }) => {
  const { active, current, setActive, setCurrent, setHovered } = useInspect();
  const [tab, setTab] = useState('home');
  const [person, setPerson] = useState(CARE_PROFILE.person);
  // Which home card is explaining itself. Held here rather than in HomeScreen
  // because the sheet belongs to the phone, not to the card.
  const [intro, setIntro] = useState<string | null>(null);
  // The flow a Continue opened. Separate from `intro` because the interstitial
  // closes as the flow opens: they are two layers, not one screen with a step.
  const [flow, setFlow] = useState<string | null>(null);
  // Bumped when a flow ends, so the profile remounts on Assess rather than on
  // whichever pill it was left on.
  const [assessKey, setAssessKey] = useState(0);

  // The calculator does not end on its last question, it ends on what it worked
  // out. Its own state rather than another flow, because the result is the same
  // layer as the chat: the modal has not closed, the flow has just moved on.
  const [calculated, setCalculated] = useState(false);

  // Whether the flow was finished or left. A layer closes the same way either
  // way, so the flow has to say which it was before it goes.
  const finished = useRef(false);

  // Where a flow puts you when it closes.
  //
  // Finished: the row it belongs to, on Assess. Not back on home, which is where
  // you started but not where the thing you just did now lives — the point of
  // the walk is that you learn where to find it again.
  //
  // Left part way: exactly where they opened it, which is home. Nothing has been
  // filled in, so there is nothing on Assess to be shown, and moving them there
  // would say something happened when it did not.
  const endFlow = () => {
    setFlow(null);
    if (finished.current) {
      setAssessKey((k) => k + 1);
      setTab(cut?.assessTab ?? 'profile');
    }
    finished.current = false;
    setCalculated(false);
  };

  // A flow's own way of saying it got to the end. Wraps the close its layer
  // handed it, so finishing and leaving are the same gesture with a different
  // landing.
  const finish = (close: () => void) => () => {
    finished.current = true;
    close();
  };

  // Continue hands the interstitial over to the flow: the flow comes up over it,
  // and the interstitial goes once it is covered.
  //
  // Closing it as the flow opened ran two 280ms slides in opposite directions at
  // the same z-index, and for the first frames the interstitial had left the top
  // of the screen while the flow was still below the fold, so home showed
  // through the gap between them. Nothing here is loading, so there is nothing
  // to fill: one layer moves and the other waits underneath it.
  const handOver = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(handOver.current), []);

  const startFlow = (card: string) => {
    setFlow(card);
    window.clearTimeout(handOver.current);
    handOver.current = window.setTimeout(() => setIntro(null), MODAL_SETTLE_MS);
  };

  const exitDrillMode = useCallback(() => {
    setActive(false);
    setCurrent(null);
    setHovered(null);
  }, [setActive, setCurrent, setHovered]);

  // Esc is staged: a first press clears a selected layer, a second leaves drill
  // mode. Matches the other stages.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (current) {
        setCurrent(null);
        setHovered(null);
      } else {
        exitDrillMode();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, current, exitDrillMode, setCurrent, setHovered]);

  const content = cut ? (
    cut.screen(tab, { person, setPerson, setTab, setIntro, assessKey })
  ) : tab === 'home' ? (
    // The master's own screens, named: the bar says whose app this is on
    // every tab rather than only on the one about them.
    <HomeScreen title={CARE_PROFILE.person} onCard={setIntro} />
  ) : tab === 'actions' ? (
    <ArchitectureActions />
  ) : tab === 'profile' ? (
    <ArchitectureProfile key={assessKey} />
  ) : tab === 'care' ? (
    <ShortlistsTab title={CARE_PROFILE.person} />
  ) : tab === 'help' ? (
    <ArchitectureHelp onTalk={() => setTab('chat')} />
  ) : (
    // Where 'Talk to us' lands. Nothing is drawn behind it yet, and a chat
    // that is not the question here should say so rather than mock one up.
    <TabPlaceholder title='Chat' />
  );

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: active ? 'custom.charcoal' : 'custom.neutral.100',
        transition: 'background-color 400ms ease',
      }}
    >
      {/* The stage, with the controls floating over its foot. */}
      <Box sx={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex' }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
            pb: STAGE_BAR_ROOM,
            overflow: 'auto',
          }}
        >
          <PhoneFrame inspecting={active}>
            <ScreenRoot>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {content}

                {/* Presented, not routed: it opens over whatever is behind
                    it and closes without leaving a trace in history. */}
                {intro && CARD_INTROS[intro] && (
                  <ModalScreen onClose={() => setIntro(null)}>
                    {(close) => (
                      <ArchitectureCardScreen
                        intro={CARD_INTROS[intro]}
                        onClose={close}
                        onStart={
                          CARD_INTROS[intro].action
                            ? () => startFlow(intro)
                            : undefined
                        }
                      />
                    )}
                  </ModalScreen>
                )}

                {/* The flow itself. Which way it arrives follows what its own
                    bar offers: the calculator closes with an X, so it comes up
                    from the bottom; the financial assessment steps back with a
                    chevron, so it comes in from the right and the gesture that
                    dismisses it is the one that matches. */}
                {flow === 'Benefits calculator' && (
                  <ModalScreen onClose={endFlow}>
                    {(close) =>
                      calculated ? (
                        // Both ways off the result are the end of the flow, so
                        // either lands on Assess, where the claim now lives.
                        <ResultScreen
                          bar={<CalculatorBar onLeave={close} />}
                          onStart={finish(close)}
                          onTalk={finish(close)}
                        />
                      ) : (
                        // The questions running out is not finishing: it hands
                        // over to the result, which is what they came for.
                        <ChatV2
                          questions={CALCULATOR_CHAT}
                          bar={<CalculatorBar onLeave={close} />}
                          onExit={close}
                          onComplete={() => setCalculated(true)}
                        />
                      )
                    }
                  </ModalScreen>
                )}

                {flow === 'Financial status' && (
                  <PushScreen onClose={endFlow}>
                    {(close) => (
                      <FinancialAssessment
                        onComplete={finish(close)}
                        onExit={close}
                      />
                    )}
                  </PushScreen>
                )}

                {/* Nothing drawn for these two yet, so they say so rather
                    than pretending. Still close the way the others do. */}
                {(flow === 'Care assessment' ||
                  flow === 'Power of attorney') && (
                  <ModalScreen onClose={endFlow}>
                    {(close) => (
                      <ScreenRoot>
                        <StatusBar />
                        <AppBar
                          title={flow}
                          align='left'
                          size='small'
                          action={
                            <IconButton
                              aria-label='Close'
                              size='small'
                              onClick={close}
                            >
                              <Icon icon={'xmark' as never} fontSize={18} />
                            </IconButton>
                          }
                        />
                        <TabPlaceholder title='Not drawn yet' />
                      </ScreenRoot>
                    )}
                  </ModalScreen>
                )}
              </Box>
              <TabBar
                items={cut?.tabs ?? TABS}
                // The chat is opened from Help and has no tab of its own, so
                // Help stays lit rather than the pill vanishing mid-flow.
                active={tab === 'chat' ? 'help' : tab}
                onSelect={(id) => {
                  setCurrent(null);
                  setHovered(null);
                  setTab(id);
                }}
              />
            </ScreenRoot>
            <InspectOverlay />
          </PhoneFrame>
        </Box>

        {/* The control, floating at the foot of the stage. */}
        <StageBar>
          <DrillModeToggle />
        </StageBar>
      </Box>

      <LayersPanel />
      <InspectPanel />
    </Box>
  );
};

const ArchitectureApp = ({ cut }: { cut?: ArchitectureCut } = {}) => (
  <InspectProvider>
    <Stage cut={cut} />
  </InspectProvider>
);

export default ArchitectureApp;
